/**
 * `/settings/sharing` (design §2.10).
 *
 *   "Two distinct grant types, shown as two tables — **do not conflate**."
 *
 * `shares.outgoing` and `shares.incoming` each return contact and list grants in
 * one flat page (the contract denormalises them deliberately: "so the
 * outgoing/incoming lists are a single flat table rather than a client-side
 * union"). The design's "do not conflate" is honoured by SEGMENTING on
 * `resourceType` — a real server-side filter — rather than by rendering one
 * undifferentiated list, so a contact grant and a list grant are never mistaken
 * for each other even though one request fetches both.
 *
 * Every write on this screen is real: `shares.contact.create`,
 * `shares.list.create`, both revokes and `shares.adopt` are all implemented in
 * `apps/api`.
 *
 * ## Invitations (landed 2026-08-29)
 *
 * Design §2.10 ends with "`ShareInviteFlow` by email; pending invites with
 * expiry". The first half is real now: `shares.invite` mints a one-time token
 * (created from the share dialog on a contact or a list) and
 * `shares.acceptInvite` redeems it — the card below is where that happens.
 *
 * The second half is still absent: no procedure lists outstanding invitations,
 * in either direction. So this screen never claims to show pending invites, and
 * the redeem card is a paste box rather than an inbox.
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  SegmentedControl,
  SkeletonRows,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@crm/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ShareIcon } from 'lucide-react';
import { useState } from 'react';
import {
  type IncomingShareRow,
  type ShareResourceType,
  type ShareSummaryRow,
  useAcceptInviteMutation,
  useIncomingSharesQuery,
  useOutgoingSharesQuery,
  useRevokeShareMutation,
} from '../../../api/sharing.ts';
import { Planned } from '../../../components/placeholders.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { AdoptDialog } from '../../../components/sharing/adopt-dialog.tsx';
import { scopeLabel } from '../../../components/sharing/share-scope.ts';
import { toClientError } from '../../../lib/errors.ts';
import { formatDate } from '../../../lib/format.ts';

export const Route = createFileRoute('/_authed/settings/sharing')({
  component: SharingSettings,
  errorComponent: RouteErrorPanel,
});

type Segment = ShareResourceType | 'all';

const SEGMENTS = [
  { value: 'all', label: 'All' },
  { value: 'contact', label: 'Contacts' },
  { value: 'list', label: 'Lists' },
] as const satisfies readonly { value: Segment; label: string }[];

function ResourceSegment({
  value,
  onChange,
}: {
  readonly value: Segment;
  readonly onChange: (next: Segment) => void;
}) {
  return (
    <SegmentedControl<Segment>
      aria-label="Grant type"
      options={SEGMENTS}
      value={value}
      onValueChange={(next) => onChange(next ?? value)}
    />
  );
}

function GranteeLabel({ share }: { readonly share: ShareSummaryRow }) {
  // A user grant carries an email, an org grant a name. Neither is guaranteed —
  // the projection can withhold both — so the id is the last honest fallback
  // rather than a fabricated "someone".
  const name =
    share.granteeType === 'org'
      ? (share.granteeOrgName ?? share.granteeOrgId)
      : (share.granteeUserEmail ?? share.granteeUserId);
  return (
    <span className="text-muted-foreground">
      {share.granteeType === 'org' ? 'workspace' : 'person'} {name ?? 'unknown'}
    </span>
  );
}

function OutgoingTable() {
  const [segment, setSegment] = useState<Segment>('all');
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const outgoing = useOutgoingSharesQuery({
    ...(segment === 'all' ? {} : { resourceType: segment }),
    includeRevoked,
  });
  const revoke = useRevokeShareMutation();

  const items = outgoing.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-3">
        <CardTitle>Grants you have made</CardTitle>
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeRevoked}
              onChange={(event) => setIncludeRevoked(event.target.checked)}
            />
            Show revoked
          </label>
          <ResourceSegment value={segment} onChange={setSegment} />
        </div>
      </CardHeader>
      <CardContent>
        {outgoing.isLoading ? (
          <SkeletonRows rows={3} />
        ) : outgoing.isError ? (
          <EmptyState kind="error" title="Could not load outgoing grants" />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ShareIcon aria-hidden />}
            title="You have not shared anything"
            description="Share a contact from its page, or a list from the list's page."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {items.map((share) => (
              <li key={share.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <Badge variant="muted">{share.resourceType}</Badge>
                {share.resourceType === 'contact' ? (
                  <Link
                    to="/contacts/$contactId"
                    params={{ contactId: share.resourceId }}
                    className="font-medium underline-offset-2 hover:underline"
                  >
                    {share.resourceLabel}
                  </Link>
                ) : (
                  <Link
                    to="/lists/$listId"
                    params={{ listId: share.resourceId }}
                    className="font-medium underline-offset-2 hover:underline"
                  >
                    {share.resourceLabel}
                  </Link>
                )}
                <span aria-hidden className="text-muted-foreground">
                  →
                </span>
                <GranteeLabel share={share} />
                <Badge variant="secondary">{scopeLabel(share.scope)}</Badge>
                {share.allowAdopt ? <Badge variant="outline">adoptable</Badge> : null}
                {share.resourceType === 'list' ? (
                  <span className="text-xs text-muted-foreground">
                    {share.contactCount} contact{share.contactCount === 1 ? '' : 's'}
                  </span>
                ) : null}
                {share.expiresAt === null ? null : (
                  <span className="text-xs text-muted-foreground">
                    expires {formatDate(share.expiresAt)}
                  </span>
                )}
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatDate(share.createdAt)}
                </span>
                {share.revokedAt === null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    disabled={revoke.isPending}
                    onClick={() =>
                      revoke.mutate({ shareId: share.id, resourceType: share.resourceType })
                    }
                  >
                    Revoke
                  </Button>
                ) : (
                  <Badge variant="destructive">revoked {formatDate(share.revokedAt)}</Badge>
                )}
              </li>
            ))}
          </ul>
        )}
        {outgoing.data?.nextCursor == null ? null : (
          <p className="mt-3 text-xs text-muted-foreground">
            Only the first page of grants is shown — more exist beyond it, and this screen does not
            page through them yet.
          </p>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Revoking deletes the grantee’s access rows immediately. A copy they already{' '}
          <em>adopted</em> is theirs and is not affected — that is what adopting means.
        </p>
      </CardContent>
    </Card>
  );
}

function IncomingTable() {
  const [segment, setSegment] = useState<Segment>('all');
  const [unadoptedOnly, setUnadoptedOnly] = useState(false);
  const [adopting, setAdopting] = useState<IncomingShareRow | null>(null);

  const incoming = useIncomingSharesQuery({
    ...(segment === 'all' ? {} : { resourceType: segment }),
    unadoptedOnly,
  });

  const items = incoming.data?.items ?? [];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center gap-3">
          <CardTitle>Grants made to you</CardTitle>
          <div className="ml-auto flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={unadoptedOnly}
                onChange={(event) => setUnadoptedOnly(event.target.checked)}
              />
              Not yet adopted
            </label>
            <ResourceSegment value={segment} onChange={setSegment} />
          </div>
        </CardHeader>
        <CardContent>
          {incoming.isLoading ? (
            <SkeletonRows rows={3} />
          ) : incoming.isError ? (
            <EmptyState kind="error" title="Could not load incoming grants" />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<ShareIcon aria-hidden />}
              title={unadoptedOnly ? 'Nothing left to adopt' : 'Nothing has been shared with you'}
              {...(unadoptedOnly
                ? {
                    kind: 'filtered' as const,
                    description: 'Every inbound grant already has a copy in this workspace.',
                  }
                : {})}
            />
          ) : (
            <ul className="flex flex-col divide-y divide-border">
              {items.map((share) => (
                <li key={share.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <Badge variant="muted">{share.resourceType}</Badge>
                  <span className="font-medium">{share.resourceLabel}</span>
                  <span className="text-xs text-muted-foreground">from {share.ownerOrgName}</span>
                  <Badge variant="secondary">{scopeLabel(share.scope)}</Badge>
                  {share.adoptedContactId !== null ? (
                    <Badge variant="success">adopted {formatDate(share.adoptedAt)}</Badge>
                  ) : share.allowAdopt ? (
                    <Badge variant="outline">adoptable</Badge>
                  ) : (
                    <Badge variant="muted">read-only</Badge>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    {share.resourceType === 'contact' ? (
                      <Button asChild size="sm" variant="ghost">
                        <Link to="/contacts/$contactId" params={{ contactId: share.resourceId }}>
                          Open
                        </Link>
                      </Button>
                    ) : (
                      <Button asChild size="sm" variant="ghost">
                        <Link to="/lists/$listId" params={{ listId: share.resourceId }}>
                          Open
                        </Link>
                      </Button>
                    )}
                    {share.adoptedContactId === null && !share.allowAdopt ? null : (
                      <Button size="sm" variant="outline" onClick={() => setAdopting(share)}>
                        {share.adoptedContactId === null ? 'Adopt…' : 'Compare…'}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {incoming.data?.nextCursor == null ? null : (
            <p className="mt-3 text-xs text-muted-foreground">
              Only the first page of inbound grants is shown — more exist beyond it, and this screen
              does not page through them yet.
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            A grant is <strong>read-only</strong>. Editing a shared record means adopting it, which
            forks a copy this workspace owns — and re-enriches it at this workspace’s cost, because
            vendor data is licensed per workspace.
          </p>
        </CardContent>
      </Card>

      {adopting === null ? null : (
        <AdoptDialog
          open
          onOpenChange={(open) => {
            if (!open) setAdopting(null);
          }}
          share={adopting}
        />
      )}
    </>
  );
}

function SharingSettings() {
  return (
    <div className="flex max-w-4xl flex-col gap-4">
      <Tabs defaultValue="outgoing">
        <TabsList>
          <TabsTrigger value="outgoing">Shared by you</TabsTrigger>
          <TabsTrigger value="incoming">Shared with you</TabsTrigger>
        </TabsList>

        <TabsContent value="outgoing">
          <OutgoingTable />
        </TabsContent>

        <TabsContent value="incoming">
          <IncomingTable />
        </TabsContent>
      </Tabs>

      <RedeemInvitation />

      <Planned
        title="Invitations you have sent or received"
        description="A list of outstanding invitations, with who they went to and when they expire, so you can chase or cancel one. Sending and accepting both work today — only the overview is missing."
      />
    </div>
  );
}

/**
 * Redeeming an invitation someone sent you.
 *
 * Two facts the copy has to carry, because getting either wrong is unrecoverable
 * for the user:
 *
 *  - the grant lands on the ACTIVE workspace, so someone with two must switch
 *    first (an invitation binds to a tenant, not to a person);
 *  - redeeming the same token twice is safe — the server returns the original
 *    grant with `alreadyAccepted: true` rather than creating a second one — which
 *    is what makes a forwarded link safe to click.
 */
function RedeemInvitation() {
  const [token, setToken] = useState('');
  const accept = useAcceptInviteMutation();
  const [result, setResult] = useState<{ contactsGranted: number; already: boolean } | null>(null);

  const failure = accept.error === null ? null : toClientError(accept.error);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Accept an invitation</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="text-muted-foreground">
          Paste an invitation somebody sent you. What they shared arrives in whichever workspace you
          have active right now — switch first if that is not the one you want.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={token}
            aria-label="Invitation"
            placeholder="Paste the invitation here"
            className="sm:flex-1"
            onChange={(event) => {
              setToken(event.target.value);
              setResult(null);
            }}
          />
          <Button
            disabled={token.trim() === '' || accept.isPending}
            onClick={() =>
              accept.mutate(token.trim(), {
                onSuccess: (redeemed) => {
                  setToken('');
                  setResult({
                    contactsGranted: redeemed.contactsGranted,
                    already: redeemed.alreadyAccepted,
                  });
                },
              })
            }
          >
            {accept.isPending ? 'Accepting…' : 'Accept'}
          </Button>
        </div>

        {result === null ? null : (
          <p role="status" className="font-medium text-foreground">
            {result.already
              ? 'You had already accepted this one — nothing changed.'
              : `Done. ${result.contactsGranted} contact${
                  result.contactsGranted === 1 ? '' : 's'
                } are now visible to this workspace.`}{' '}
            <Link
              to="/contacts"
              search={{ source: 'shared' }}
              className="underline underline-offset-2"
            >
              See what was shared
            </Link>
          </p>
        )}

        {failure === null ? null : (
          <p role="alert" className="font-medium text-destructive">
            {failure.message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
